/* ══════════════════════════════════════════════════════════════════════════
   edge-data.js — activation, Edge OS interfaces, positioning, dispatch.

   Contract sources (exact files read):
     the identity check · the identity check
     the provisioning lifecycle · the provisioning lifecycle
     the device challenge · the factory register · the device lifecycle
     the capability registry · verification · brand wiring
     the location service · locations_pg/* · the audit trail
     responder dispatch · responder_dispatch/* · incident cell roles
     the dispatch rules · the dispatch rules
   Design source (no backend yet — marked as such in the UI):
     docs/plans/2026-07-30-croc-edge-os-master-design.md §5.8 W8 · §5.5 W5 · §5.1 W1
   ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
   A · ACTIVATION — the identity check returns one of six states
   ══════════════════════════════════════════════════════════════════════════ */

export const IDENTIFY_CASES = [
  { id:"ready_factory", short:"Ready to claim", tone:"go", label:"Ready · factory MAC on file",
    say:"The factory manifest already carries this board's MAC. You can claim it now, and the device does not have to be powered on or reachable.",
    answer:"Known board. Its MAC is already on file, so you can take custody straight away.",
    mac:"A0B7651F2C44", fw:"2.8.1", next:"claim",
    hints:["Power the device from PoE or 5 V", "Claim now, there is no network to wait for", "Bring it online within 24 h"] },

  { id:"ready", short:"Checked in", tone:"go", label:"Ready · device has checked in",
    say:"The board has finished its first check-in and is sitting unclaimed in the pending queue. This is the ordinary happy path.",
    answer:"Known board. It reported in on its own and is waiting for someone to take custody.",
    mac:"A0B7651F2C44", fw:"2.8.1", seen:"03:11:40", next:"claim", hints:[] },

  { id:"offline", short:"Never seen", tone:"warn", label:"Registered, but never seen",
    say:"The serial is registered and no MAC has ever been seen against it. Nothing can be claimed until the board checks in for the first time, and a MAC cannot be typed in by hand.",
    answer:"Known serial, no MAC yet. This board has never reported in.",
    next:"wait",
    hints:["Connect Ethernet or configure Wi-Fi", "Wait for the LED to go solid", "Refresh the pending queue"] },

  { id:"already_registered", short:"Already claimed", tone:"warn", label:"Already claimed",
    say:"This serial belongs to a tenant. Only a superadmin sees which one. Claiming it again raises the credential version and forces the running device to resync.",
    answer:"Claimed on 2026-06-14, and not by you. CS-0A41 is the id it already carries.",
    next:"blocked", hints:[] },

  { id:"unknown_serial", short:"Not on file", tone:"bad", label:"Not in the factory manifest",
    say:"The serial is well formed and no manufacturing record matches it. Either it was never registered or it belongs to another production run.",
    answer:"The serial reads correctly, and nothing was manufactured under it.",
    next:"blocked", hints:[] },

  { id:"blocked", short:"Blocked", tone:"bad", label:"Blocked at the factory",
    say:"The factory marked this unit as a return or disabled it outright. Claiming is refused until a superadmin lifts the block.",
    answer:"This unit was blocked at the factory. Custody cannot be taken while the block stands.",
    next:"blocked", hints:[] },
];

export const PENDING_QUEUE = [
  { mac:"A0B7651F2C44", qr:"CROC|SN-K4XQ…|1780…", fw:"2.8.1", seen:"12 s ago",  proposed:"CS-1A07", fresh:true },
  { mac:"A0B7651F3B18", qr:"CROC|SN-K4XR…|1780…", fw:"2.8.1", seen:"4 m ago",   proposed:"CS-1A08", fresh:true },
  { mac:"A0B7650E9D02", qr:"CROC-A0B7650E9D02",   fw:"2.7.4", seen:"1 h 12 m",  proposed:"CS-1A09" },
  { mac:"A0B765112E71", qr:"CROC|SN-K4TP…|1779…", fw:"2.7.4", seen:"9 h 40 m",  proposed:"CS-1A0B" },
];

/* every way a claim can be refused, said the way the operator will read it */
export const CLAIM_ERRORS = [
  { tone:"",     d:"The MAC is malformed",                   why:"It has to be twelve hexadecimal characters." },
  { tone:"",     d:"The device id is not an accepted shape",  why:"It does not meet the serial format rule." },
  { tone:"",     d:"The device id does not match the serial", why:"Checked whenever factory registration is enforced." },
  { tone:"",     d:"The MAC is not the one on file",          why:"This board is not the one that shipped as this serial." },
  { tone:"",     d:"The QR signature did not verify",         why:"The code was altered, or it was never signed at the factory." },
  { tone:"",     d:"Nothing is waiting under that MAC",       why:"No board with that MAC is sitting in the pending queue." },
  { tone:"warn", d:"That device id is taken",                 why:"Ids are unique across the whole platform. Choose another." },
  { tone:"warn", d:"The board is already claimed",            why:"Another tenant holds custody of it." },
  { tone:"warn", d:"The board has not proven its key",        why:"Proof of key is armed, so the challenge has to pass first." },
  { tone:"bad",  d:"The credential could not be saved",       why:"Nothing was claimed. Nothing was left half-written either." },
];

export const LIFECYCLE_STATES = [
  { id:"ACTIVE",    tone:"go",   say:"Claimed, credentialled, allowed on the broker." },
  { id:"UNBOUND",   tone:"warn", say:"Custody removed. The board comes back as unprovisioned when it next checks in." },
  { id:"RESETTING", tone:"warn", say:"A reset command is on its way to the board." },
  { id:"OFFLINE",   tone:"" ,    say:"No heartbeat inside the window." },
];

export const FACTORY_AXIS = [
  { id:"unclaimed", tone:"",     say:"Manufactured and registered. Claimable." },
  { id:"claimed",   tone:"go",   say:"A tenant holds custody." },
  { id:"blocked",   tone:"bad",  say:"Returned or disabled at the factory. Terminal until a superadmin lifts it." },
];

/* ══════════════════════════════════════════════════════════════════════════
   B · EDGE OS BOARD — Waveshare ESP32-P4-WIFI6-POE-ETH Rev2.0

   Every millimetre below was read from the vendor's own sources, not arranged
   for a picture. Provenance:

     · Board outline 55 × 68 mm — Waveshare dimension drawing
     · Connector and silicon positions — vector assembly drawings on pages 2–3
       of ESP32-P4-WIFI6-POE-ETH-Schematic.pdf, scaled so the outline is exact
     · 40-pin table — official pinout overlay, verified net-for-net against the
       schematic's P6 symbol
     · Origin: bottom-left of the PCB as seen from the top (component) side,
       X right, Y up. The 40-pin header sits on the top edge; RJ45 / USB /
       microSD sit on the bottom edge.

   Z heights are not published by Waveshare. Where a height appears it is the
   part's typical datasheet figure, tagged `zSrc:"typical"`, and must never be
   treated as a measured number from this board.
   ══════════════════════════════════════════════════════════════════════════ */

export const BOARD = {
  sku:"ESP32-P4-WIFI6-POE-ETH", rev:"Rev2.0",
  mcu:"ESP32-P4NRW32 · dual-core RISC-V @360 MHz + LP @40 MHz",
  module:"Bare QFN under shield · ESP32-P4NRW32 · 32 MB NOR (GD25Q256)",
  radio:"ESP32-C6-MINI-1U · Wi-Fi 6 + BLE 5 over SDIO",
  psram:"32 MB in-package", flash:"32 MB NOR",
  eth:"100 Mbps · IP101GRI PHY · RJ45 HBJ-6117ANL with integral magnetics",
  size:"55 × 68 mm", header:"2 × 20 · 2.54 mm · 28 programmable GPIO",
  tier:"T2", note:"One camera per node — hard constraint D4.",
  source:"waveshare wiki · schematic PDF pp.2–3 · pinout overlay",
};

/* The board seen from above. Portrait: header at the top, RJ45 at the bottom.
   An earlier revision of this file had the axes swapped (68 × 55), which made
   every percentage below it a lie against the unit in someone's hand. */
export const BOARD_MM = { w:55, h:68 };

/* Convert a box given in millimetres (Y-up, origin bottom-left) into the
   percentages the renderer uses (Y-down, origin top-left). Keeping the
   conversion in one place means a coordinate can only be wrong in one place. */
export const mmBox = (xa, ya, xb, yb) => ({
  x: +(xa / BOARD_MM.w * 100).toFixed(3),
  y: +((BOARD_MM.h - yb) / BOARD_MM.h * 100).toFixed(3),
  w: +((xb - xa) / BOARD_MM.w * 100).toFixed(3),
  h: +((yb - ya) / BOARD_MM.h * 100).toFixed(3),
});
export const mmPt = (x, y) => ({
  x: +(x / BOARD_MM.w * 100).toFixed(3),
  y: +((BOARD_MM.h - y) / BOARD_MM.h * 100).toFixed(3),
});

/* Four M3 clearance holes. Centres from the vector drawing; pitch 48 × 33 mm
   is the only interior dimension Waveshare itself prints on the drawing. */
export const HOLES_MM = [
  { x:3.50,  y:24.31 }, { x:51.50, y:24.31 },
  { x:3.50,  y:57.30 }, { x:51.50, y:57.30 },
];

/* Connectors. Ranges are the silkscreen body from the vector assembly drawing.
   A negative Y means the part overhangs the board edge — keep it, that is how
   the RJ45 and the USB stack sit on the real unit. */
/* Soft≠Live Page30: geometry + labels only. Soft brochure bound/state
   (fake camera titles, Soft radio uplink, state live) deleted — Live GET …/ports owns truth. */
export const PORTS = [
  { id:"gpio", ...mmBox(2.11, 61.47, 52.89, 66.55),
    label:"40-pin header", full:"2 × 20 · 2.54 mm · 28 programmable GPIO", cls:"io",
    bound:null, state:"unknown",
    detail:"Where every sensor and actuator lands. Twelve of the forty seats are power and ground.",
    lane:"Capability plane", z:8.5, zSrc:"typical" },
  { id:"pwr5v", ...mmBox(6.86, 55.40, 10.60, 59.21),
    label:"5 V in", full:"1.0 mm 2-pin terminal", cls:"power",
    bound:null, state:"unknown",
    detail:"Alternative to PoE. Powering both at once is not a supported arrangement.",
    lane:null, z:4, zSrc:"typical" },
  { id:"dsi", ...mmBox(0.66, 31.75, 5.51, 53.75),
    label:"MIPI-DSI", full:"15-pin 1.0 mm FFC · display", cls:"bus",
    bound:null, state:"unknown", detail:"Display FFC footprint. Live state comes from GET …/ports.",
    lane:null, z:1.5, zSrc:"typical" },
  { id:"mipi_csi", ...mmBox(7.16, 31.75, 12.01, 53.75),
    label:"MIPI-CSI", full:"15-pin 1.0 mm FFC · camera + ISP", cls:"bus",
    bound:null, state:"unknown",
    detail:"Form A frame source. Exactly one camera per node. Camera titles only when Live reports them.",
    lane:"Video Runtime", z:1.5, zSrc:"typical" },
  { id:"c6_sdio", ...mmBox(18.41, 24.84, 31.61, 37.34),
    label:"ESP32-C6", full:"ESP32-C6-MINI-1U · Wi-Fi 6 + BLE 5 over SDIO", cls:"radio",
    bound:null, state:"unknown",
    detail:"A separate radio module on this board, reached over SDIO. Uplink state only when Live reports it.",
    lane:"Uplink", z:2.4, zSrc:"espressif datasheet" },
  { id:"uart_h", ...mmBox(14.37, 17.02, 20.19, 21.30),
    label:"UART", full:"SH1.0 4-pin · TXD RXD GND VCC", cls:"io",
    bound:null, state:"unknown", detail:"Flashing and console. Not a runtime interface.",
    lane:null, z:3, zSrc:"typical" },
  { id:"i2c_h", ...mmBox(6.30, 17.02, 12.11, 21.30),
    label:"I²C", full:"SH1.0 4-pin · SCL SDA GND VCC", cls:"bus",
    bound:null, state:"unknown",
    detail:"The same bus as header seats 3 and 5, brought out on a cable. Device counts only when Live reports them.",
    lane:"Capability plane", z:3, zSrc:"typical" },
  { id:"usbh", ...mmBox(5.69, -1.65, 18.84, 16.03),
    label:"USB-A · Type-C", full:"USB 2.0 OTG HS · Type-C console beneath", cls:"io",
    bound:null, state:"unknown",
    detail:"Type-A and Type-C are one stacked part. The Type-C below is the console and can also power the board.",
    lane:null, z:15, zSrc:"typical" },
  { id:"eth_poe", ...mmBox(21.94, -1.78, 37.94, 19.71),
    label:"RJ45 · PoE", full:"HBJ-6117ANL · 100 Mbps + integral magnetics", cls:"power",
    bound:null, state:"unknown",
    detail:"Primary uplink and the node's power source, on one cable. Link duplex only when Live reports it.",
    lane:"μDock · MQTT", z:13.5, zSrc:"typical" },
  { id:"sd", ...mmBox(39.36, 0.30, 51.17, 11.41),
    label:"microSD", full:"SDIO 3.0 friction slot", cls:"storage",
    bound:null, state:"unknown",
    detail:"Cold store only. Never on an inference path. Storage capacity only when Live reports it.",
    lane:"Capsule Loader", z:1.8, zSrc:"typical" },
  { id:"c6uart", ...mmBox(51.85, 1.40, 54.44, 11.56),
    label:"C6 console", full:"2.54 mm pads · TXD RXD GND IO9", cls:"io",
    bound:null, state:"unknown", detail:"Service pads for the radio. Not for field use.",
    lane:null, z:0.8, zSrc:"typical" },
];

/* Authoritative 40-pin table. Transcribed from the vendor pinout overlay and
   verified against the schematic's P6 symbol — the two agree completely.
   Power and ground positions are Raspberry-Pi-pin-compatible.
   Odd seats are the INNER row (toward the middle of the board); even seats
   are the OUTER row (toward the top edge). The board's own silkscreen prints
   "1"/"2" the other way around — that is a silkscreen error, not the pinout. */
const SEAT = {
  1:{ kind:"rail", label:"3V3" },              2:{ kind:"rail", label:"5V" },
  3:{ kind:"gpio", label:"GPIO7",  gpio:7  },  4:{ kind:"rail", label:"5V" },
  5:{ kind:"gpio", label:"GPIO8",  gpio:8  },  6:{ kind:"gnd",  label:"GND" },
  7:{ kind:"gpio", label:"GPIO23", gpio:23 },  8:{ kind:"gpio", label:"GPIO37", gpio:37 },
  9:{ kind:"gnd",  label:"GND" },             10:{ kind:"gpio", label:"GPIO38", gpio:38 },
 11:{ kind:"gpio", label:"GPIO21", gpio:21 }, 12:{ kind:"gpio", label:"GPIO22", gpio:22 },
 13:{ kind:"gpio", label:"GPIO20", gpio:20 }, 14:{ kind:"gnd",  label:"GND" },
 15:{ kind:"gpio", label:"GPIO6",  gpio:6  }, 16:{ kind:"gpio", label:"GPIO5",  gpio:5  },
 17:{ kind:"rail", label:"3V3" },             18:{ kind:"gpio", label:"GPIO4",  gpio:4  },
 19:{ kind:"gpio", label:"GPIO3",  gpio:3  }, 20:{ kind:"gnd",  label:"GND" },
 21:{ kind:"gpio", label:"GPIO2",  gpio:2  }, 22:{ kind:"gpio", label:"GPIO1",  gpio:1  },
 23:{ kind:"gpio", label:"GPIO0",  gpio:0  }, 24:{ kind:"gpio", label:"GPIO36", gpio:36 },
 25:{ kind:"gnd",  label:"GND" },             26:{ kind:"gpio", label:"GPIO32", gpio:32 },
 27:{ kind:"gpio", label:"GPIO24", gpio:24 }, 28:{ kind:"gpio", label:"GPIO25", gpio:25 },
 29:{ kind:"gpio", label:"GPIO33", gpio:33 }, 30:{ kind:"gnd",  label:"GND" },
 31:{ kind:"gpio", label:"GPIO26", gpio:26 }, 32:{ kind:"gpio", label:"GPIO54", gpio:54 },
 33:{ kind:"gpio", label:"GPIO48", gpio:48 }, 34:{ kind:"gnd",  label:"GND" },
 35:{ kind:"gpio", label:"GPIO53", gpio:53 }, 36:{ kind:"gpio", label:"GPIO46", gpio:46 },
 37:{ kind:"gpio", label:"GPIO47", gpio:47 }, 38:{ kind:"gpio", label:"GPIO27", gpio:27 },
 39:{ kind:"gnd",  label:"GND" },             40:{ kind:"gpio", label:"GPIO45", gpio:45 },
};

/* Pin 1 centre at (3.37, 62.74); columns step 2.54 mm; odd = inner Y 62.74,
   even = outer Y 65.28. Body-derived grid from the vector drawing. */
export const seatMm = seat => {
  const col = Math.floor((seat - 1) / 2);
  return { x: 3.37 + col * 2.54, y: seat % 2 === 1 ? 62.74 : 65.28 };
};
export const seatPct = seat => mmPt(seatMm(seat).x, seatMm(seat).y);

export const HEADER = Array.from({ length:40 }, (_,i)=>{
  const seat = i + 1;
  return { seat, ...SEAT[seat], ...seatPct(seat) };
});

/* Consumed before anyone gets a say. The last three rows are the trap: those
   GPIOs land on the header AND are wired to onboard parts through 0 Ω links,
   so driving them from the header will disturb the part. */
export const PINS_SPOKEN_FOR = [
  { fn:"Ethernet RMII",              pins:"GPIO 28–31, 34, 35, 49–52", header:false },
  { fn:"SDIO to ESP32-C6",           pins:"GPIO 14–19",                header:false },
  { fn:"TF card SDIO",               pins:"GPIO 39–44",                header:false },
  { fn:"I²S audio (ES8311)",         pins:"GPIO 9–13",                 header:false },
  { fn:"I²C bus (shared with H1)",   pins:"GPIO 7, 8 · seats 3, 5",    header:true,  trap:"codec also sits here" },
  { fn:"Amplifier enable PA_CTRL",   pins:"GPIO 53 · seat 35",         header:true,  trap:"0 Ω to NS4150B" },
  { fn:"C6 chip-enable CHIP_PU",     pins:"GPIO 54 · seat 32",         header:true,  trap:"0 Ω to C6" },
  { fn:"C6 IO2",                     pins:"GPIO 6 · seat 15",          header:true,  trap:"0 Ω to C6" },
];

/* GPIOs that reach the header but are already spoken for. The binder must
   surface this before anyone confirms a binding — an override is allowed, and
   it is logged against the person who pressed it. */
export const PIN_TRAPS = {
  6:  { why:"Wired through 0 Ω to the C6's IO2. Driving it from the header talks to the radio." },
  7:  { why:"Shared I²C with the ES8311 codec. An extra device is fine; a different role is not." },
  8:  { why:"Shared I²C with the ES8311 codec. An extra device is fine; a different role is not." },
  53: { why:"Amplifier enable. Holding this pin low silences the speaker chain." },
  54: { why:"C6 chip-enable. Holding this pin resets the radio." },
};

/* Soft≠Live: Soft BOUND brochure roles deleted. Geometry seats start unbound;
   Live GET /devices/{id}/pins hydrates role/state via mergeLiveSeatsOntoSoftPins. */
const FREE_CLS = { 37:"execute", 38:"execute", 20:"execute", 4:"read", 3:"read", 2:"read",
                   1:"read", 0:"read", 24:"bus", 25:"bus", 33:"bus", 26:"bus",
                   48:"read", 46:"read", 47:"read", 27:"read", 45:"read",
                   6:"read", 53:"execute", 54:"execute" };

export const PINS = HEADER.filter(h=>h.kind==="gpio").map(h=>{
  const trap = PIN_TRAPS[h.gpio] || null;
  return {
    pin:h.label, seat:h.seat, gpio:h.gpio, trap, override:null,
    fn:null, role:null, cls:FREE_CLS[h.gpio] || "read", state:"unassigned",
    probe:"awaiting Live pinmap", live:null,
  };
});

/* what a pin may be bound to. read → auto-probe. execute → human confirm. */
export const PIN_ROLES = [
  { id:"pir",    label:"PIR motion",       cls:"read",    cap:null,       say:"Digital in, rising edge." },
  { id:"door",   label:"Door contact",     cls:"read",    cap:"door",     say:"Dry contact, pull-up." },
  { id:"tamper", label:"Tamper switch",    cls:"read",    cap:null,       say:"Normally closed." },
  { id:"temp",   label:"Temperature",      cls:"read",    cap:null,       say:"1-Wire or analogue." },
  { id:"vibe",   label:"Vibration",        cls:"read",    cap:null,       say:"Digital in, debounced." },
  { id:"siren",  label:"Siren relay",      cls:"execute", cap:"siren",    say:"Drives a coil. Confirm before arming." },
  { id:"gate",   label:"Gate / door strike",cls:"execute",cap:"gate",     say:"Opens a physical barrier.", risk:"high" },
  { id:"pa",     label:"Public address",   cls:"execute", cap:"speaker",  say:"Audio out to a horn.", risk:"high" },
  { id:"i2c",    label:"I²C bus",          cls:"bus",     cap:null,       say:"Shared, addressed." },
  { id:"spi",    label:"SPI bus",          cls:"bus",     cap:null,       say:"Shared, chip-selected." },
];

/* CANONICAL_CAPABILITIES — the capability registry */
export const CAP_CATALOG = [
  { id:"snapshot",     cls:"read",    say:"Pull a still frame on demand." },
  { id:"video",        cls:"read",    say:"Continuous stream available." },
  { id:"night_vision", cls:"read",    say:"IR-capable sensor." },
  { id:"thermal",      cls:"read",    say:"Thermal imager." },
  { id:"ptz",          cls:"execute", say:"Pan, tilt or zoom the optics." },
  { id:"move",         cls:"execute", say:"The resource can relocate itself." },
  { id:"talk",         cls:"execute", say:"Two-way audio." },
  { id:"speaker",      cls:"execute", say:"One-way audio out.", risk:"high" },
  { id:"siren",        cls:"execute", say:"Audible alarm.", risk:"high" },
  { id:"door",         cls:"execute", say:"Release a door.", risk:"high" },
  { id:"gate",         cls:"execute", say:"Release a vehicle gate.", risk:"high" },
];

export const DEVICE_TYPES = [
  { id:"croc_sentinel", label:"Sentinel panel",   caps:["siren","speaker"] },
  { id:"camera_hik",    label:"Hikvision camera", caps:["snapshot","video","night_vision","ptz"] },
  { id:"camera_dahua",  label:"Dahua camera",     caps:["snapshot","video","night_vision","ptz"] },
  { id:"camera_generic",label:"Generic camera",   caps:["snapshot"] },
  { id:"human_responder",label:"Human responder", caps:["move","talk"] },
  { id:"edge_ai_node",  label:"Edge AI node",     caps:["snapshot","video","night_vision"], design:true },
];

export const CAP_TRUTH = {
  gate:"the capability registry",
  def:"the shared platform registry",
  say:"When the capability registry is on, claims live in the shared database. Verified only means claimed and driver-tested — never mark verified locally.",
  phrase:"a capability registry that survives a restart and refuses local verified invent",
};

/* ══════════════════════════════════════════════════════════════════════════
   C · POSITIONING (W5) — where the truth may come from, and where it may not
   ══════════════════════════════════════════════════════════════════════════ */

export const POS_SOURCES = [
  { rank:1, id:"survey", label:"Install-time survey", who:"Fixed nodes", err:"≈ 0 m", state:"live",
    say:"An engineer records site, building, floor, zone and local x,y at mount time. This is the primary truth for anything bolted down.",
    answer:"kept as the node's fixed position, marked as set by hand" },
  { rank:2, id:"ftm", label:"Wi-Fi FTM · 802.11mc", who:"New nodes", err:"4 – 8 m ❓", state:"design",
    say:"Round-trip timing against already-surveyed anchors. Blocked on confirming the on-board C6 silicon revision, since ECO1 and earlier carry no FTM Initiator.",
    answer:"not built yet" },
  { rank:3, id:"rssi", label:"Multi-anchor RSSI", who:"Refinement only", err:"coarse", state:"design",
    say:"May nudge a position that already exists. It may never create one, and it may never move a fix across a boundary.",
    answer:"not built yet" },
  { rank:4, id:"phone", label:"Responder phone GPS", who:"People", err:"metres", state:"live",
    say:"The only moving-position source that exists today. Consent-gated, retention-bounded, and the input to every dispatch ranking.",
    answer:"accepted only from a responder's own phone" },
];

export const POS_FORBIDDEN = [
  { id:"geoip",  label:"GeoIP / IP → city" },
  { id:"cell",   label:"Carrier cell triangulation" },
  { id:"mccmnc", label:"MCC-MNC lookup" },
  { id:"guess",  label:"Any P4-side inferred latitude" },
];

export const POS_LAW = {
  title:"An address is not a position",
  say:"A responder standing in Sarawak whose traffic leaves through Kuala Lumpur is in Sarawak. Any source that would answer Kuala Lumpur is refused at the write path — it is never stored, and it never reaches a dispatch ranking.",
  cite:"Owner ruling 2026-07-31 · Edge OS design §5.5",
};

export const GPS_QUALITY = [
  { k:"Batch ceiling",        v:"50 points at a time" },
  { k:"Accepted source",      v:"a responder's phone, nothing else" },
  { k:"Null island",          v:"(0, 0) is refused" },
  { k:"Clock skew",           v:"beyond the clock-skew limit" },
  { k:"Implausible speed",    v:"faster than a person can travel" },
  { k:"Accuracy floor",       v:"too imprecise to act on" },
  { k:"Out-of-order",         v:"points that arrive out of sequence" },
  { k:"Who it belongs to",    v:"never taken from the phone" },
];

/* ══════════════════════════════════════════════════════════════════════════
   D · DISPATCH — Soft assign / rules rehearsal seeds.
   Live board: GET /responder-dispatches (admin) or …/mine (responder).
   ══════════════════════════════════════════════════════════════════════════ */

export const CANDIDATES = [
  { rank:1, id:"h.tan",   label:"Hafiz Tan",   km:1.23, m:1234, stale:false, on:true,  busy:false,
    reason:"distance 1.23 km", eligible:true },
  { rank:2, id:"r.lim",   label:"Regina Lim",  km:2.81, m:2814, stale:false, on:true,  busy:true,
    reason:"distance 2.81 km; busy", eligible:false, why:"busy" },
  { rank:3, id:"s.wong",  label:"Sook Wong",   km:3.44, m:3441, stale:false, on:true,  busy:false,
    reason:"distance 3.44 km", eligible:true },
  { rank:4, id:"m.zaid",  label:"Marwan Zaid", km:4.02, m:4019, stale:false, on:false, busy:false,
    reason:"distance 4.02 km; off call", eligible:false, why:"off_call" },
  { rank:5, id:"a.kumar", label:"Anand Kumar", km:5.90, m:5903, stale:true,  on:true,  busy:false,
    reason:"distance 5.90 km; stale GPS", eligible:false, why:"stale" },
];

export const DISPATCH_RULES = [
  { id:"dr-motion-p1", name:"Perimeter motion after hours", obs:"motion", metric:"risk_score",
    op:"gte", threshold:60, kind:"human_responder", action:"dispatch", prio:10,
    enabled:true, rev:7, certified:"2026-07-14 · s.wong" },
  { id:"dr-door-ajar", name:"Door held open", obs:"contact", metric:"open_seconds",
    op:"gte", threshold:120, kind:"human_responder", action:"dispatch", prio:20,
    enabled:true, rev:3, certified:"2026-06-30 · s.wong" },
  { id:"dr-cam-dark",  name:"Camera unreachable", obs:"health", metric:"offline_seconds",
    op:"gte", threshold:600, kind:"human_responder", action:"notify", prio:40,
    enabled:true, rev:12, certified:null, drift:true },
  { id:"dr-siren-auto",name:"Auto siren on repeat", obs:"motion", metric:"repeat_count",
    op:"gte", threshold:3, kind:"sentinel_nexus", action:"actuate", prio:5,
    enabled:false, rev:2, certified:null, reason:"awaiting commissioning" },
];

/* incident cell roles */
export const CELL_ROLES = [
  { id:"scout",     prefer:["snapshot","ptz","night_vision"], res:"CS-0A41 · Cam 04",  kind:"camera", fit:0.92, aff:0.88, state:"active" },
  { id:"perimeter", prefer:["door","siren","gate"],           res:"CS-0B12 · Gate 02", kind:"contact",fit:0.71, aff:0.79, state:"active" },
  { id:"responder", prefer:["move","talk","siren"],           res:"h.tan · Hafiz Tan", kind:"person", fit:0.84, aff:0.84, state:"active" },
  { id:"recorder",  prefer:["snapshot","video"],              res:"CS-0D03 · Cam 11",  kind:"camera", fit:0.66, aff:0.66, state:"pending" },
  { id:"command",   prefer:["talk","speaker"],                res:null,                kind:null,     fit:0,    aff:0,    state:"vacated" },
];

/* ══════════════════════════════════════════════════════════════════════════
   E · μDock live session (W1) — event pops a real stream, 5 min TTL
   ══════════════════════════════════════════════════════════════════════════ */

export const UDOCK = {
  ttl:300, brands:["Hikvision","Dahua","Uniview","VIGI"],
  laws:[
    ["Outbound only",   "The node never listens. 80, 443, 554, 8554, 8555, 1984 and 8889 are all refused."],
    ["Zero decode",     "The gateway forwards bytes. It never decodes, transcodes or draws a box."],
    ["Hard expiry",     "Five minutes, then the tunnel closes and the grant is withdrawn."],
    ["Renewal audited", "Extending writes an audit row. It is not silent."],
    ["Alarm wins",      "Uplink alarms outrank the live tunnel whenever bandwidth is contested."],
  ],
};
